import React, { useCallback, useEffect } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Box, ClickAwayListener } from '@mui/material';
import { ParticipantsIcon } from '@icons';

import { isScheduledEvent } from '@helpers/events';
import ParticipantNumberInfo from './ParticipantsContent/ParticipantNumberInfo';
import { useSearchUserContext } from '@providers/SearchUserProvider';
import {
  Header,
  ModalContainer,
  Tabs,
} from '@core/dashboard/components/PopoverModal';
import ParticipantsContent from './ParticipantsContent';
import { useMytaverseEvent } from '@core/dashboard/providers';
import OVERLAYS from '@constants/overlays';
import HomeBarButton from '@core/dashboard/components/HomeBarButton';
import { FAMILIARISE_STATE } from '@core/dashboard/components/HomeBottomBar/constants';
import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import { useParticipantList } from '@core/dashboard/components/HomeBottomBar/Participants/context/context';
import { useOverlay } from '@providers/OverlayProvider';
import { AnalyticsType } from '@interfaces/analytics';
import { useClientSpecificDashboardButtonDisabled } from '@hooks/clientSpecificUtils';
import { DashboardButtonEnum } from '@constants/dashboard';

const OVERLAY_NAME = OVERLAYS.PARTICIPANTS;

const Participants = ({ t: translate }: WithTranslation) => {
  const {
    focusOnSearchField,
    onFindTapHeader,
    onBackHeader,
    searchResultNumber,
  } = useSearchUserContext();
  const { currentEvent, trackEventAnalytics, familiarizeState } =
    useMytaverseEvent();

  const isDisabledClientSpecific = useClientSpecificDashboardButtonDisabled(
    DashboardButtonEnum.PARTICIPANTS_BUTTON,
  );

  const { open, setOpen, toggleOpen } = useParticipantList();

  const { handleClose, handleToggle } = useOverlay(OVERLAY_NAME, open, setOpen);

  const getHeader = (onClose: any) => {
    if (isMobileOnly && !focusOnSearchField) {
      return (
        <>
          <Header
            text={translate(
              currentEvent && isScheduledEvent(currentEvent)
                ? 'participantsModal.participants'
                : 'participantsModal.community',
            )}
            onClose={onClose}
            isBig
            onFind={onFindTapHeader}
          />
          {currentEvent && isScheduledEvent(currentEvent) && (
            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                }}
              >
                <ParticipantNumberInfo />
              </Box>
              <Tabs
                active={'insession'}
                tabs={[
                  {
                    label: translate('participantsModal.inSession'),
                    value: 'insession',
                  },
                ]}
                onChange={() => {}}
              />
            </Box>
          )}
          {(!currentEvent || !isScheduledEvent(currentEvent)) && (
            <Tabs
              active={'hub'}
              tabs={[
                {
                  label: translate('participantsModal.hub'),
                  value: 'hub',
                },
              ]}
              onChange={() => {}}
            />
          )}
        </>
      );
    } else if (isMobileOnly && focusOnSearchField) {
      return (
        <Header
          text={translate(`participantsModal.searchResults`, {
            number: searchResultNumber,
          })}
          onClose={onClose}
          isBig
          onBack={onBackHeader}
        />
      );
    } else {
      return (
        <>
          <Header
            text={translate('participantsModal.community')}
            onClose={onClose}
            isBig
          />
          <Tabs
            active={'hub'}
            tabs={[{ label: translate('participantsModal.hub'), value: 'hub' }]}
            onChange={() => {}}
          />
        </>
      );
    }
  };

  const handleToggleIcon = () => {
    handleToggle();
    toggleOpen();
  };

  useEffect(() => {
    if (open) {
      trackEventAnalytics(AnalyticsType.NavbarButtonClick, {
        name: 'participants',
      });
    }
  }, [open, trackEventAnalytics]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (familiarizeState?.name) {
          return;
        }
        handleClose();
      }}
    >
      <Box>
        <HomeBarButton
          label={translate('participantsModal.tooltip')}
          icon={<ParticipantsIcon />}
          isActive={
            open && familiarizeState?.name !== FAMILIARISE_STATE.TRAINER_TOOLS
          }
          handleClick={handleToggleIcon}
          disabled={isDisabledClientSpecific}
        />

        <SwipeableDrawer
          position={{
            vertical: isMobileOnly ? 'top' : 'bottom',
            horizontal: 'right',
          }}
          open={open}
          onClose={handleClose}
          onOpen={() => setOpen(true)}
        >
          <ModalContainer isScrollable={false} header={getHeader(handleClose)}>
            <ParticipantsContent onClose={handleClose} />
          </ModalContainer>
        </SwipeableDrawer>
      </Box>
    </ClickAwayListener>
  );
};

export default withTranslation()(Participants);
